import {
   capsForModel,
   DEFAULT_GEMINI_TTS_MODEL,
   GEMINI_TTS_MODELS,
   type GeminiTtsModel,
} from "@/constants/voiceModels";
import {VoiceProvider} from "@/enums/agencies";
import {
   type ElevenLabsVoiceSettingsConfig,
   type GeminiVoiceSettingsConfig,
   type PhoneScript,
   type VoiceSettingsConfig,
} from "@/types/campaigns";

export const GEMINI_VOICE_DIRECTION_MAX_CHARS = 500;

export function supportsVoiceDirection(provider: VoiceProvider): boolean {
   return provider === VoiceProvider.Gemini;
}

export function geminiSettings(
   settings: VoiceSettingsConfig | null | undefined,
): GeminiVoiceSettingsConfig | undefined {
   if (settings?.provider !== VoiceProvider.Gemini) return undefined;
   return {
      provider: VoiceProvider.Gemini,
      model: geminiModel(settings.model),
      ...(typeof settings.style === "string" ? {style: settings.style} : {}),
   };
}

function geminiModel(model: string | null | undefined): GeminiTtsModel {
   return model && GEMINI_TTS_MODELS.has(model)
      ? model as GeminiTtsModel
      : DEFAULT_GEMINI_TTS_MODEL;
}

/** Keep a script's internal model while updating its customer-facing direction. */
export function withGeminiVoiceDirection(script: PhoneScript, direction: string): PhoneScript {
   const current = geminiSettings(script.voiceSettings);
   return {
      ...script,
      voiceSettings: {
         provider: VoiceProvider.Gemini,
         model: geminiModel(current?.model),
         ...(direction ? {style: direction} : {}),
      },
   };
}

/** A new provider cannot inherit another provider's tuning fields. */
export function withSelectedVoice(
   script: PhoneScript,
   voice: {id: string; provider: VoiceProvider},
): PhoneScript {
   const sameProvider = script.voiceProvider === voice.provider;
   return {
      ...script,
      voice: voice.id,
      voiceProvider: voice.provider,
      voiceSettings: sameProvider ? script.voiceSettings : undefined,
   };
}

/**
 * Build the wire-ready voice settings payload.
 *
 * Returns `undefined` when there's nothing to send (null/undefined input),
 * which callers use to omit `voiceSettings` from the request entirely
 * (= fall back to LD defaults).
 *
 * When present, always includes `provider` and only the non-null slider
 * fields the current model supports. Galactus rejects unsupported fields
 * (`ERR_VOICE_SETTINGS_FIELD_NOT_SUPPORTED_FOR_MODEL`) — e.g. `style` on
 * `eleven_flash_v2_5`, or speed/similarity/style on v3.
 *
 * When `model` is omitted, every non-null field is kept (backend treats
 * them as supported). An unrecognized id is not treated as omitted: tuning
 * fields are reduced to `stability` (the one field every known model
 * accepts); `provider` and the given `model` are still sent.
 */
export function toVoiceSettingsPayload(
   settings: VoiceSettingsConfig | null | undefined,
   provider: VoiceProvider
): VoiceSettingsConfig | undefined {
   if (!settings) return undefined;

   if (provider === VoiceProvider.Gemini && settings.provider === VoiceProvider.Gemini) {
      const style = typeof settings.style === "string"
         ? settings.style.trim().slice(0, GEMINI_VOICE_DIRECTION_MAX_CHARS)
         : "";
      return {
         provider: VoiceProvider.Gemini,
         model: geminiModel(settings.model),
         ...(style ? {style} : {}),
      };
   }

   // Keep the legacy save-boundary normalization for older ElevenLabs drafts
   // whose settings discriminator was another non-Gemini provider. A Gemini
   // text direction must never be reinterpreted as ElevenLabs slider tuning.
   if (provider !== VoiceProvider.ElevenLabs || settings.provider === VoiceProvider.Gemini) {
      return undefined;
   }

   const caps = capsForModel(settings.model);
   const modelOmitted = !settings.model;
   const allowSpeed = modelOmitted || !!caps?.speed;
   const allowSimilarity = modelOmitted || !!caps?.similarity;
   const allowStyle = modelOmitted || !!caps?.style;

   const payload: ElevenLabsVoiceSettingsConfig = {provider: VoiceProvider.ElevenLabs};
   if (settings.model) payload.model = settings.model;
   if (allowSpeed && settings.fastSpeed != null) payload.fastSpeed = settings.fastSpeed;
   if (allowSpeed && settings.qualitySpeed != null) payload.qualitySpeed = settings.qualitySpeed;
   if (settings.stability != null) payload.stability = settings.stability;
   if (allowSimilarity && settings.similarityBoost != null) payload.similarityBoost = settings.similarityBoost;
   if (allowStyle && typeof settings.style === "number") payload.style = settings.style;
   return payload;
}

/**
 * Sanitize `script.voiceSettings` at the save boundary.
 *
 * - Drops `voiceSettings` for unsupported providers and mismatched Gemini settings.
 * - Normalizes older ElevenLabs drafts to the script's provider.
 * - Strips null slider fields so only set values hit the wire.
 * - Strips slider fields the current model does not support.
 */
export function sanitizeVoiceSettings(script: PhoneScript): PhoneScript {
   if (!script.voiceSettings) return script;

   if (script.voiceProvider !== VoiceProvider.ElevenLabs && script.voiceProvider !== VoiceProvider.Gemini) {
      return {...script, voiceSettings: undefined};
   }

   return {
      ...script,
      voiceSettings: toVoiceSettingsPayload(script.voiceSettings, script.voiceProvider),
   };
}
